const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';

// Scroll in animation logic
function onIntersection(elements, observer) {
  elements.forEach((element, index) => {
    if (element.isIntersecting) {
      const elementTarget = element.target;
      if (elementTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        elementTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (elementTarget.hasAttribute('data-cascade'))
          elementTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(elementTarget);
    } else {
      element.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
    }
  });
}

function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    threshold: 0.18,
  });
  animationTriggerElements.forEach((element) => observer.observe(element));
}

// Zoom in animation logic
function initializeScrollZoomAnimationTrigger() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const animationTriggerElements = Array.from(document.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));

  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    let elementIsVisible = false;
    const observer = new IntersectionObserver((elements) => {
      elements.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    window.addEventListener(
      'scroll',
      throttle(() => {
        if (!elementIsVisible) return;

        element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
      }),
      { passive: true }
    );
  });
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) {
    // If we haven't reached the image yet
    return 0;
  } else if (elementPositionY + elementHeight < scrollY) {
    // If we've completely scrolled past the image
    return 100;
  }

  // When the image is in the viewport
  const distance = scrollY + viewportHeight - elementPositionY;
  let percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  initializeScrollAnimationTrigger();
  initializeScrollZoomAnimationTrigger();
});

/* Safety net for the reveal above.

   The observer is built on DOMContentLoaded — before images have loaded and
   the final layout is known. Anything measured as off-screen at that instant
   gets .scroll-trigger--offscreen, which drops it to opacity 0.01 and holds it
   there until the visitor scrolls. On a slow connection that is a blank
   product grid on first paint: the cards are in the viewport, but the browser
   decided otherwise a moment earlier.

   After load, sweep anything still marked off-screen that is in fact within
   the viewport and let it animate. Elements genuinely below the fold keep the
   class and still reveal on scroll, so the intended behaviour is untouched. */
window.addEventListener('load', () => {
  requestAnimationFrame(() => {
    document.querySelectorAll(`.${SCROLL_ANIMATION_OFFSCREEN_CLASSNAME}`).forEach((element) => {
      const rect = element.getBoundingClientRect();
      const inViewport = rect.top < window.innerHeight && rect.bottom > 0 && rect.height > 0;
      if (inViewport) element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
  });
});

/* Product-card reveal — "Settle" (docs/design/card-motion-study.md).

   Deliberately not built on the scroll-trigger machinery above. That system
   hides an element first and waits for an observer to un-hide it, so a late or
   missing observer leaves a blank grid — the failure the post-load sweep above
   exists to catch. This only ever *adds* an entrance: the card is visible in
   CSS, and the worst thing that can happen here is that it appears without
   animating.

   Cards already on screen at load are left alone. They arrive with the page,
   and animating what the visitor is already looking at is what produces the
   flash of content this file has had to work around before.

   What counts as "already on screen" is read live, inside the callback: if the
   page is still sitting at the top when a card first intersects, that card came
   with the page. A card skipped this way is deliberately NOT unobserved — only
   a card that actually animates is — so if it was misjudged it gets another
   chance the next time it enters the viewport.

   Three things this is deliberately NOT built on, all three tried and all three
   wrong in a different way:

   - Measuring getBoundingClientRect() at DOMContentLoaded. Nothing has settled
     then: every card can measure as inside the viewport and none is ever
     observed. On a 375px viewport that meant not one card animated.
   - Waiting for a scroll event. If it never arrives the flag never flips and
     no card ever animates — a silent, total no-op on a page that still looks
     correct, which is the hardest kind of failure to notice. It does not fire
     under emulation, which is reason enough not to stake the feature on it.
   - Trusting the observer's first pass and unobserving on it. Same flaw as
     measuring, one step later: on the homepage the hero has not sized when that
     pass runs, the product row measures as visible, and unobserving there
     retires every card permanently. */
const SETTLE_CLASS = 'kashira-settle--in';
const SETTLE_STAGGER_MS = 70;
const SETTLE_MAX_ORDER = 4;

function initializeSettleReveal(rootEl = document) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const cards = Array.from(rootEl.querySelectorAll('.product-card-wrapper:not([data-settle])'));
  if (cards.length === 0) return;

  const observer = new IntersectionObserver((entries, obs) => {
    /* A row of cards crosses the threshold in one callback, so ordering by
       position within the batch staggers along the row and resets on the next
       one — rather than counting up through the whole grid, which would put a
       card on the fourth row two seconds behind. */
    let order = 0;
    const atTop = (window.scrollY || document.documentElement.scrollTop || 0) <= 0;
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      // The page has not moved, so this card came with it. Stay observed.
      if (atTop) return;
      obs.unobserve(entry.target);
      const delay = Math.min(order, SETTLE_MAX_ORDER) * SETTLE_STAGGER_MS;
      entry.target.style.setProperty('--kashira-settle-delay', `${delay}ms`);
      entry.target.classList.add(SETTLE_CLASS);
      order += 1;
    });
  }, { threshold: 0.15 });

  cards.forEach((card) => {
    card.setAttribute('data-settle', '');
    observer.observe(card);
  });
}

/* Take the class off once the longest of the three animations has finished, so
   the card is back to its plain resting state and can be revealed again if it
   is ever re-observed. The photograph settles over 900ms, against the card's
   620ms and the text's 640ms. */
document.addEventListener('animationend', (event) => {
  if (event.animationName !== 'kashira-settle-photo') return;
  const card = event.target.closest && event.target.closest('.product-card-wrapper');
  if (!card) return;
  card.classList.remove(SETTLE_CLASS);
  card.style.removeProperty('--kashira-settle-delay');
});

window.addEventListener('DOMContentLoaded', () => initializeSettleReveal());

/* Filtering swaps the whole grid for a fresh one (assets/facets.js:147). Most
   of what it inserts is already on screen and so is skipped, but a tall result
   set still has rows below the fold. */
document.addEventListener('kashira:facets-rendered', () => initializeSettleReveal());

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => initializeScrollAnimationTrigger(event.target, true));
  document.addEventListener('shopify:section:reorder', () => initializeScrollAnimationTrigger(document, true));
}
